import { BackoffMode, CacheMode } from '../types';
import { createAbortError, createPromise, proxyResponseWithAdditionalNext, AbortErrorName, } from './utils';
import { SWRFetcherRequest } from './swr';
/**
 * ROE request, handles request with retries
 */
export class ROEFetcherRequest {
    constructor(requestControl, cacheKey, options, request, logger) {
        this.requestControl = requestControl;
        this.cacheKey = cacheKey;
        this.options = options;
        this.request = request;
        this.logger = logger;
        this.isAborted = false;
        this.retryControl = new RetryControl(options);
    }
    run() {
        const responseControls = createPromise();
        // for abort call to resolve abort error
        this.responseResolve = responseControls.resolve;
        // use swr for first request
        this.innerRequest = new SWRFetcherRequest(this.requestControl, this.cacheKey, this.options, this.request, this.logger);
        this.innerRequest.run().then((response) => {
            if (this.isAborted) {
                responseControls.resolve(createAbortError());
                return;
            }
            responseControls.resolve(proxyResponseWithAdditionalNext(response, ({ error }) => {
                var _a;
                if (this.isAborted ||
                    !error ||
                    error.name === AbortErrorName ||
                    !this.retryControl.canRetry()) {
                    return;
                }
                // start retry
                const nextControls = createPromise();
                // to let abort work on next promise
                this.responseResolve = nextControls.resolve;
                const retryLoop = () => {
                    var _a;
                    (_a = this.logger) === null || _a === void 0 ? void 0 : _a.call(this, 'start retry');
                    // do retry
                    this.innerRequest = new SWRFetcherRequest(this.requestControl, this.cacheKey, 
                    // no cache for these requests - but saves result to cache
                    Object.assign({}, this.options, { cacheMode: CacheMode.NoCache }), this.request);
                    this.innerRequest.run().then(({ data, fromCache, error }) => {
                        var _a;
                        if (this.isAborted || (error === null || error === void 0 ? void 0 : error.name) === AbortErrorName) {
                            nextControls.resolve(createAbortError());
                            return;
                        }
                        if (error) {
                            if (!this.retryControl.canRetry()) {
                                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.call(this, 'run out retry times');
                                // no retry times, resolve error
                                nextControls.resolve({ error });
                                return;
                            }
                            this.retryControl.retry(retryLoop);
                            return;
                        }
                        nextControls.resolve({ data, fromCache });
                    });
                };
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.call(this, 'retry scheduled');
                this.retryControl.retry(retryLoop);
                return nextControls.promise;
            }));
        });
        return responseControls.promise;
    }
    abort() {
        var _a, _b, _c;
        if (this.isAborted) {
            return;
        }
        this.isAborted = true;
        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.call(this, 'aborted');
        (_b = this.responseResolve) === null || _b === void 0 ? void 0 : _b.call(this, createAbortError());
        (_c = this.innerRequest) === null || _c === void 0 ? void 0 : _c.abort();
        this.retryControl.reset();
    }
}
class RetryControl {
    constructor(options) {
        this.options = options;
        this.retriedTimes = 0;
        this.reset();
    }
    reset() {
        clearTimeout(this.retryTimeout);
        this.retriedTimes = 0;
    }
    canRetry() {
        return this.retriedTimes < this.options.retryTimes;
    }
    retry(callback) {
        var _a;
        if (!this.canRetry()) {
            return;
        }
        clearTimeout(this.retryTimeout);
        let nextRetryWaitTime;
        switch (this.options.retryBackoff) {
            case BackoffMode.JitteredExponential:
                nextRetryWaitTime =
                    this.options.retryInitialWaitTime * randomBetween(1, Math.pow(2, this.retriedTimes));
                break;
            case BackoffMode.Exponential:
                nextRetryWaitTime = this.options.retryInitialWaitTime * Math.pow(2, this.retriedTimes);
                break;
            case BackoffMode.Constant:
            default:
                nextRetryWaitTime = this.options.retryInitialWaitTime;
                break;
        }
        // cap retry wait time by max wait time
        nextRetryWaitTime = Math.min(nextRetryWaitTime, (_a = this.options.retryMaxWaitTime) !== null && _a !== void 0 ? _a : Number.MAX_VALUE);
        this.retryTimeout = setTimeout(callback, nextRetryWaitTime * 1000);
        this.retriedTimes += 1;
    }
}
function randomBetween(min, max) {
    return min + Math.random() * (max - min);
}
